import React, { ChangeEvent, CSSProperties, useCallback, useRef } from 'react';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import { FieldColorNames } from 'Hooks/DocumentFields/useDocumentFieldColor';
import { DatePipeOptions } from 'Interfaces/Common';
import { Signer } from 'Interfaces/Document';
import { DocumentField } from 'Interfaces/DocumentFields';

import NameIcon from 'Assets/images/icons/name-icon.svg';

interface NameFieldProps {
  onFieldDataChange?: (fieldData: Partial<DocumentField>) => void;
  signer?: Signer;
  value?: string | null;
  style?: CSSProperties;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  isInsertable?: boolean;
  fieldColor?: FieldColorNames;
  datePipeOptions?: DatePipeOptions;
}

export const NameField = ({
  value,
  signer,
  onFieldDataChange,
  style,
  onFocus,
  onBlur,
  fieldColor,
  disabled,
  isInsertable,
}: NameFieldProps) => {
  const shadowRef = useRef<HTMLSpanElement>(null);

  const placeholder = signer?.isPreparer
    ? 'Your name'
    : signer?.name ??
      (signer?.role
        ? `${signer.role}${signer.role.endsWith('s') ? "'" : "'s"} name`
        : 'Name');

  const changeTextValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;

      if (shadowRef.current) {
        shadowRef.current.textContent = next || placeholder;
      }

      if (onFieldDataChange) {
        onFieldDataChange({ text: next });
      }
    },
    [onFieldDataChange, placeholder],
  );

  return (
    <div
      style={style}
      className={classNames('fieldDropDown__trigger-name', `fieldColor__${fieldColor}`, {
        'fieldDropDown__trigger--disabled': disabled,
        disabled_pointer_event: disabled || !isInsertable,
      })}
    >
      <span
        ref={shadowRef}
        className="fieldDropDown__trigger-name-content fieldDropDown__trigger-name-shadow"
      >
        {value || placeholder}
      </span>
      <input
        onFocus={onFocus}
        onBlur={onBlur}
        type="text"
        className={classNames(
          'fieldDropDown__trigger-name-content',
          'fieldDropDown__trigger-name-input',
          {
            'fieldDropDown__trigger--disabled': disabled,
          },
        )}
        placeholder={placeholder}
        defaultValue={value || ''}
        onChange={changeTextValue}
        disabled={disabled || !isInsertable}
      />
      <ReactSVG src={NameIcon} className="fieldDropDown__trigger-name-icon" />
    </div>
  );
};

export default NameField;
